import { useEffect, useRef, useState } from 'react';
import { type AcceptedFileType } from '@/utils/acceptedFileAttributes';
import {
  dragContainsFiles,
  inferDragState,
  type DragState,
} from '@/utils/dragSupport.util';

interface UseFileDropOptions {
  fileType: AcceptedFileType | AcceptedFileType[];
  // When false, no window listeners are attached and `dragState` stays 'idle'.
  // Lets a shared component opt into drag tracking only where it's wanted.
  enabled?: boolean;
}

interface UseFileDropResult {
  dragState: DragState;
}

// Tracks, live, whether a file is being dragged over the window and whether it
// matches `fileType` ('idle' | 'supported' | 'unsupported'). The drop itself is
// left to the consumer's own target element (a card, an upload box), which
// keeps this hook a pure detector with no opinion on what happens on drop.
const useFileDrop = ({
  fileType,
  enabled = true,
}: UseFileDropOptions): UseFileDropResult => {
  const [dragState, setDragState] = useState<DragState>('idle');

  // Keep the latest fileType in a ref so the window listeners are registered
  // exactly once. Re-subscribing on every render would reset the enter/leave
  // counter mid-drag and make the drag state flicker.
  const fileTypeRef = useRef(fileType);
  useEffect(() => {
    fileTypeRef.current = fileType;
  });

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    // dragenter/leave bubble per child, so count enter/leave deltas instead of
    // relying on a single boolean. dragover supplies the freshest MIME info.
    let counter = 0;

    const handleEnter = (event: DragEvent) => {
      if (!dragContainsFiles(event.dataTransfer)) {
        return;
      }
      counter += 1;
      setDragState(inferDragState(event.dataTransfer, fileTypeRef.current));
    };
    const handleOver = (event: DragEvent) => {
      if (!dragContainsFiles(event.dataTransfer)) {
        return;
      }
      setDragState(inferDragState(event.dataTransfer, fileTypeRef.current));
    };
    const handleLeave = (event: DragEvent) => {
      if (!dragContainsFiles(event.dataTransfer)) {
        return;
      }
      counter = Math.max(0, counter - 1);
      if (counter === 0) {
        setDragState('idle');
      }
    };
    const handleDrop = () => {
      counter = 0;
      setDragState('idle');
    };

    window.addEventListener('dragenter', handleEnter);
    window.addEventListener('dragover', handleOver);
    window.addEventListener('dragleave', handleLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragenter', handleEnter);
      window.removeEventListener('dragover', handleOver);
      window.removeEventListener('dragleave', handleLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [enabled]);

  return { dragState };
};

export default useFileDrop;
